import { ConfigProvider, Divider, Modal, Switch } from 'antd';
import { BaseButton } from '../UI/BaseButton';
import { useState, useEffect, useRef, useCallback } from 'react';
import TaskTypeDropdown from '../UI/Dropdown/TaskTypeDropdown';
import { TaskType } from '../../types/TaskType';
import ParentTaskDropdown from '../UI/Dropdown/ParentTaskDropdown';
import { useCreateTask, useGetTasks } from '../../serverState/tasks';
import { addLabelsToTask } from '../../api/task.api';
import { insertInSpaceEpicsOrder, insertInSpaceTasksOrder } from '../../serverState/helpers/tasksOrderHelper';
import { useGetActualSpaceId, useGetUsersWithWorkspaceAccess, useGetWorkspaceById, useGetWorkspaces } from '../../serverState/workspace';
import Task from '../../types/Task';
import { useEpics } from '../../serverState/epics';
import { Epic } from '../../types/Epic';
import { SprintStatusType } from '../UI/SprintStatusLabel';
import { WorkspaceEstimationType, WorkspaceFramework } from '../../types/Workspace';
import { User } from '../../types/User';
import { PriorityOptions } from '../TaskTable/helpers/helper';
import { defaultTaskStatuses, TaskStatus } from '../../types/TaskStatus';
import { usePlanningSessionById } from '../../serverState/planning';
import { queryClient } from '../../queryClient';
import { useActiveOrganization } from '../../common/auth';
import BaseDropdown from '../UI/Dropdown/BaseDropdown';
import EpicDropdown from '../UI/Dropdown/EpicDropdown';
import colorTokens from '../../constants/design_system/colors';
import { TitleEditableField } from '../../pages/Task/field/TitleEditableField';
import SprintStatusDropdown from '../UI/Dropdown/SprintStatusDropdown';
import OwnerDropdown from '../UI/Dropdown/OwnerDropdown';
import TaskStatusDropdown from '../UI/Dropdown/TaskStatusDropdown';
import { DeadlineEditableField } from '../../pages/Task/field/DeadlineEditableField';
import PriorityDropdown from '../UI/Dropdown/PriorityDropdown';
import EstimateDropdown from '../UI/Dropdown/EstimateDropdown';
import LabelDropdown from '../UI/Dropdown/LabelDropdown';
import { WorkspaceViewGroup } from '../../types/WorkspaceView';
import { getWorkspaceViewGroup } from '../../common/viewUtils';
import { SpaceLabel } from '../../types/TaskLabel';
import { RichTextEditor } from '../../pages/Task/components/editor/RichTextEditor';
import { UnsavedChangesModal } from './UnsavedChangesModal';
import { resolveCreateSpaceId } from './createItemSpace';
import { settleCreatedTask } from './settleCreatedTask';

interface CreateItemModalProps {
  open: boolean;
  onCancel: () => void;
  confirmLoading: boolean;
  showMessage: (createdTask: Task) => void;
  initialTitle?: string;
  parentTask?: Task;
  epicId?: number;
  taskType?: TaskType;
  sprintStatus?: SprintStatusType;
  assignee?: User;
  status?: TaskStatus;
  startDate?: Date;
  deadline?: Date;
  priority?: PriorityOptions;
  estimate?: number;
  labels?: SpaceLabel[];
  insertBeforeTaskId?: number;
  showSpaceSelector?: boolean;
  /** Space of the parent task/epic. A subitem is always created here, never in the persisted current space. (#2884) */
  parentSpaceId?: number;
}

export const CreateItemModal = (props: CreateItemModalProps) => {
  const { open, onCancel, confirmLoading, showMessage } = props;

  const actualSpaceId = useGetActualSpaceId();
  const { data: org } = useActiveOrganization();
  const workspacesQuery = useGetWorkspaces(props.showSpaceSelector ? (org?.id ?? null) : null);

  // In the cross-space (All Tasks) modal, seed the Space with the space currently
  // selected in the sidebar instead of leaving it empty — its value drives the
  // space-specific property dropdowns, which otherwise stay hidden. #2460
  const defaultSpaceId: number | null = props.showSpaceSelector && actualSpaceId ? Number(actualSpaceId) : null;
  const [selectedSpaceId, setSelectedSpaceId] = useState<number | null>(defaultSpaceId);
  const [spaceDropdownOpen, setSpaceDropdownOpen] = useState(false);
  const effectiveSpaceId = resolveCreateSpaceId({
    showSpaceSelector: props.showSpaceSelector,
    selectedSpaceId,
    actualSpaceId,
    parentSpaceId: props.parentSpaceId ?? props.parentTask?.workspaceId ?? null,
  });
  // In the cross-space (All Tasks) modal, space-specific property values are unknown until a space
  // is picked, so those dropdowns stay hidden until then. Dates and priority are space-agnostic.
  const propertiesReady = !props.showSpaceSelector || !!selectedSpaceId;

  const tasksQuery = useGetTasks(effectiveSpaceId);
  const epicsQuery = useEpics(effectiveSpaceId);
  const workspaceQuery = useGetWorkspaceById(effectiveSpaceId || null);
  const usersWithAccessToSpaceQuery = useGetUsersWithWorkspaceAccess(effectiveSpaceId || null);
  const framework = workspaceQuery.data?.framework;
  const estimationType = workspaceQuery.data?.estimationType ?? WorkspaceEstimationType.storyPoints;
  const activeSprintId = workspaceQuery.data?.activeSprintId;
  const planningSessionId = usePlanningSessionById(effectiveSpaceId || null).data?.session.id;
  const sprintStatuses: SprintStatusType[] =
    framework === WorkspaceFramework.scrum
      ? activeSprintId
        ? ['triage', 'none', 'planning', 'sprint', 'archive']
        : ['triage', 'none', 'planning', 'archive']
      : activeSprintId
        ? ['triage', 'none', 'kanban', 'archive']
        : ['triage', 'none', 'archive'];
  const taskStatuses: TaskStatus[] = (workspaceQuery.data?.TaskStatus as TaskStatus[]) || defaultTaskStatuses;
  const spaceLabels: SpaceLabel[] = (workspaceQuery.data?.Labels as SpaceLabel[]) || [];
  const group: WorkspaceViewGroup = getWorkspaceViewGroup(location.pathname);
  const locationSprintStatus: SprintStatusType | null =
    group === WorkspaceViewGroup.backlog ||
    group === WorkspaceViewGroup.archive ||
    group === WorkspaceViewGroup.epics
      ? null
      : (group as SprintStatusType);

  const [title, setTitle] = useState(props.initialTitle ?? 'New Task');
  const [taskType, setTaskType] = useState<TaskType>(props.taskType ?? TaskType.task);
  const descriptionContainerRef = useRef<HTMLDivElement>(null);
  const [parentTask, setParentTask] = useState<Task | null>(props.parentTask ?? null);
  const [epic, setEpic] = useState<Epic | null>(
    props.epicId || props.parentTask?.epicId
      ? (epicsQuery.data?.find((epic) => epic.id === (props.epicId ?? props.parentTask?.epicId)) ?? null)
      : null
  );
  const [sprintStatus, setSprintStatus] = useState<SprintStatusType | null>(
    props.sprintStatus ?? (locationSprintStatus ? locationSprintStatus : 'none')
  );
  const [assignee, setAssignee] = useState<User | null>(props.assignee ?? null);
  const [status, setStatus] = useState<TaskStatus>(
    props.status ?? taskStatuses.find((status) => status.default) ?? taskStatuses[0]
  );
  const [startDate, setStartDate] = useState<Date | null>(props.startDate ?? null);
  const [deadline, setDeadline] = useState<Date | null>(props.deadline ?? null);
  const [priority, setPriority] = useState<PriorityOptions | null>(props.priority ?? null);
  const [estimate, setEstimate] = useState<number | null>(props.estimate ?? null);
  const [labels, setLabels] = useState<SpaceLabel[]>(props.labels ?? []);
  const [description, setDescription] = useState<string>('');
  const [keepCreating, setKeepCreating] = useState<boolean>(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState<boolean>(false);
  const inputRef = useRef<any>(null);

  const createTaskMutation = useCreateTask();

  useEffect(() => {
    if (open && inputRef.current) {
      preselectTitle();
    }
  }, [open]);

  useEffect(() => {
    if (!props.showSpaceSelector || !selectedSpaceId) return;
    setParentTask(null);
    setEpic(null);
    setLabels([]);
    setSprintStatus('none');
  }, [selectedSpaceId]);

  useEffect(() => {
    if (!props.showSpaceSelector || !workspaceQuery.data) return;
    const newStatuses = (workspaceQuery.data.TaskStatus as TaskStatus[]) || defaultTaskStatuses;
    const defaultStatus = newStatuses.find((s) => s.default) ?? newStatuses[0];
    if (defaultStatus) setStatus(defaultStatus);
  }, [workspaceQuery.data?.id]);

  const preselectTitle = () => {
    if (inputRef.current) {
      inputRef.current.focus();
      const length = inputRef.current.value.length;
      inputRef.current.setSelectionRange(0, length);
    }
  };

  const defaultTitle = props.initialTitle ?? 'New Task';
  const defaultTaskType = props.taskType ?? TaskType.task;
  const defaultSprintStatus = props.sprintStatus ?? (locationSprintStatus ? locationSprintStatus : 'none');
  const defaultStatus = props.status ?? taskStatuses.find((s) => s.default) ?? taskStatuses[0];

  const hasChanges = useCallback(() => {
    // Only the Space differing from its default (the sidebar space) counts as a
    // change — selecting/keeping the default must not trigger the unsaved-changes
    // dialog when every other field is back at its default. #2459
    if (props.showSpaceSelector && selectedSpaceId !== defaultSpaceId) return true;
    if (title !== defaultTitle) return true;
    if (taskType !== defaultTaskType) return true;
    if ((parentTask?.id ?? null) !== (props.parentTask?.id ?? null)) return true;
    if ((epic?.id ?? null) !== (props.epicId ?? props.parentTask?.epicId ?? null)) return true;
    if (sprintStatus !== defaultSprintStatus) return true;
    if ((assignee?.id ?? null) !== (props.assignee?.id ?? null)) return true;
    if (status.id !== defaultStatus.id) return true;
    if (startDate !== (props.startDate ?? null)) return true;
    if (deadline !== (props.deadline ?? null)) return true;
    if (priority !== (props.priority ?? null)) return true;
    if (estimate !== (props.estimate ?? null)) return true;
    if (labels.length !== (props.labels?.length ?? 0)) return true;
    if (description !== '') return true;
    return false;
  }, [selectedSpaceId, defaultSpaceId, title, taskType, parentTask, epic, sprintStatus, assignee, status, startDate, deadline, priority, estimate, labels, description]);

  const handleClose = () => {
    if (hasChanges()) {
      setShowUnsavedModal(true);
    } else {
      onCancel();
    }
  };

  const handleCreate = async () => {
    // Prevent double-clicks and rapid successive clicks
    if (isCreating) {
      return;
    }

    setIsCreating(true);

    try {
      let epicId = epic?.id;
      let parentTaskId = parentTask?.id;
      let sprintId = sprintStatus === 'sprint' || sprintStatus === 'kanban' ? activeSprintId : undefined;
      let taskPlanningSessionId = sprintStatus === 'planning' ? planningSessionId : undefined;
      let statusId: number | undefined = status.id;
      let taskPriority = priority ?? undefined;
      let taskEstimate = estimationType === WorkspaceEstimationType.tshirtSizes ? (estimate ?? undefined) : undefined;
      let taskStoryPoints =
        estimationType === WorkspaceEstimationType.storyPoints ? (estimate ?? undefined) : undefined;
      if (taskType === TaskType.epic) {
        epicId = undefined;
        parentTaskId = undefined;
        sprintId = undefined;
        taskPlanningSessionId = undefined;
        statusId = undefined;
        taskPriority = undefined;
        taskEstimate = undefined;
        taskStoryPoints = undefined;
      }
      const createdTask = await createTaskMutation.mutateAsync({
        workspaceId: effectiveSpaceId,
        epicId,
        title,
        type: taskType,
        parentTaskId,
        sprintId,
        planningSessionId: taskPlanningSessionId,
        statusId,
        priority: taskPriority,
        estimate: taskEstimate,
        storyPoints: taskStoryPoints,
        startDate: startDate?.toISOString() ?? undefined,
        deadline: deadline?.toISOString() ?? undefined,
        assignee: assignee?.id,
        description: description || undefined,
        stage: sprintStatus === 'triage' && taskType !== TaskType.epic ? 'triage' : undefined,
      });

      // Ordering and labels are independent of each other, so they run together
      // and are both still awaited: the task is complete and correctly placed
      // the moment it appears. The cache refresh is fired and NOT awaited,
      // because `['tasks', spaceId]` is active while the modal is open and
      // awaiting its invalidation blocked the close on a full task-list
      // refetch. The create response and the taskAdded event have already put
      // the task on screen by then. (#3177)
      await settleCreatedTask({
        insertIntoOrder: () =>
          taskType === TaskType.epic
            ? insertInSpaceEpicsOrder(effectiveSpaceId, createdTask.id, epicsQuery.data?.[0]?.id)
            : insertInSpaceTasksOrder(effectiveSpaceId, createdTask.id, props.insertBeforeTaskId),
        attachLabels:
          labels.length > 0
            ? () => addLabelsToTask(createdTask.id, { labelIds: labels.map((label) => label.id) })
            : null,
        refreshCaches: () => {
          void queryClient.invalidateQueries({ queryKey: ['tasks', effectiveSpaceId] });
          if (parentTaskId) {
            void queryClient.invalidateQueries({ queryKey: ['taskSubtasks', parentTaskId] });
          }
          if (epicId) {
            void queryClient.invalidateQueries({ queryKey: ['taskSubtasks', epicId] });
          }
          if (taskType === TaskType.epic) {
            void queryClient.invalidateQueries({ queryKey: ['epics', Number(actualSpaceId)] });
          }
        },
      });
      showMessage(createdTask);
      if (keepCreating) {
        resetForm();
      } else {
        onCancel();
      }
    } catch (error) {
      // Error is already handled by the mutation, but we need to reset the creating state
      console.error('Failed to create task:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const resetForm = () => {
    resetFields();
    setTitle('New Task');
    setTaskType(props.taskType ?? TaskType.task);
    setTimeout(() => {
      preselectTitle();
    }, 100);
  };

  const resetFields = () => {
    setParentTask(props.parentTask ?? null);
    setEpic(
      props.epicId || props.parentTask?.epicId
        ? (epicsQuery.data?.find((epic) => epic.id === (props.epicId ?? props.parentTask?.epicId)) ?? null)
        : null
    );
    setSprintStatus(props.sprintStatus ?? (locationSprintStatus ? locationSprintStatus : 'none'));
    setAssignee(props.assignee ?? null);
    setStatus(props.status ?? taskStatuses.find((status) => status.default) ?? taskStatuses[0]);
    setStartDate(props.startDate ?? null);
    setDeadline(props.deadline ?? null);
    setPriority(props.priority ?? null);
    setEstimate(props.estimate ?? null);
    setLabels(props.labels ?? []);
    setDescription('');
  };

  const taskTitle = taskType === TaskType.epic ? 'Epic' : parentTask ? 'Subitem' : 'Item';

  const focusDescriptionEditor = () => {
    const editable = descriptionContainerRef.current?.querySelector('[contenteditable="true"]');
    if (editable instanceof HTMLElement) {
      editable.focus();
    }
  };

  // While a dropdown/date picker is open or the description is focused, the first click on a
  // different control only dismisses the active one (no focus, no opening another); a second click
  // acts. Title is excluded — it focuses on the first click as before. (bug #2314)
  const swallowNextClickRef = useRef(false);

  const handleBodyMouseDownCapture = (e: React.MouseEvent) => {
    swallowNextClickRef.current = false;

    const target = e.target as Node;
    const titleEl = inputRef.current as HTMLElement | null;
    if (titleEl && titleEl.contains(target)) return;

    // antd portals dropdowns/pickers to <body>, so detect open ones in the DOM.
    const openPopup = document.querySelector(
      '.ant-dropdown:not(.ant-dropdown-hidden), .ant-picker-dropdown:not(.ant-picker-dropdown-hidden), .ant-select-dropdown:not(.ant-select-dropdown-hidden)'
    );
    const isPopupOpen = openPopup instanceof HTMLElement;
    const clickInsidePopup = isPopupOpen && openPopup.contains(target);

    const active = document.activeElement as HTMLElement | null;
    const descEl = descriptionContainerRef.current;
    const isDescActive = !!descEl && !!active && descEl.contains(active);

    if (!isPopupOpen && !isDescActive) return;
    if (clickInsidePopup) return;
    if (isDescActive && descEl!.contains(target)) return;

    // Swallow: don't stopPropagation on mousedown so an open antd popup still closes on the outside
    // mousedown; the click is swallowed in handleBodyClickCapture.
    e.preventDefault();
    if (isDescActive) active!.blur();
    swallowNextClickRef.current = true;
  };

  const handleBodyClickCapture = (e: React.MouseEvent) => {
    if (!swallowNextClickRef.current) return;
    swallowNextClickRef.current = false;
    e.preventDefault();
    e.stopPropagation();
  };

  // The description can still grab focus on the mousedown default action / antd focus restore;
  // blur it while a swallow is armed.
  const handleBodyFocusCapture = (e: React.FocusEvent) => {
    if (!swallowNextClickRef.current) return;
    const target = e.target as HTMLElement;
    const descEl = descriptionContainerRef.current;
    if (!!descEl && descEl.contains(target)) {
      target.blur();
    }
  };

  return (
    <>
    <Modal
      title={
        <div className="flex items-center justify-start text-brand-grey-dark text-base font-semibold gap-2">
          Create
          <div className="h-6 flex items-center rounded-md border border-solid border-neutral-300">
            <TaskTypeDropdown
              type={taskType}
              size="medium"
              onBlur={() => {}}
              onSelect={async (type) => {
                setTaskType(type);
              }}
              allowEpic={true}
            />
          </div>
          {taskType !== TaskType.epic && (
            <div className="flex items-center gap-2">
              in
              <ParentTaskDropdown
                task={parentTask}
                options={(tasksQuery.data ?? []).filter((t) => t.parentTaskId === null && t.stage !== 'triage')}
                epics={epicsQuery.data ?? []}
                onBlur={() => {}}
                onSelect={async (parentTask) => {
                  setParentTask(parentTask ?? null);
                  setEpic(
                    parentTask?.epicId ? (epicsQuery.data?.find((epic) => epic.id === parentTask.epicId) ?? null) : null
                  );
                }}
                size="medium"
              />
            </div>
          )}
        </div>
      }
      open={open}
      confirmLoading={confirmLoading}
      onCancel={handleClose}
      centered
      width={1068}
      className="create-item-modal"
      styles={{
        header: { padding: '16px 28px 12px', margin: 0 },
        footer: { padding: '12px 28px 16px', margin: 0 },
        body: { padding: 0, borderTop: '1px solid var(--color-neutral-300)', borderBottom: '1px solid var(--color-neutral-300)' },
      }}
      footer={
        <div className="flex items-center justify-between">
          <BaseButton type="secondary" danger size="medium" onClick={handleClose}>
            Cancel
          </BaseButton>
          <div className="flex items-center gap-4">
            <ConfigProvider
              theme={{
                token: {
                  colorPrimary: colorTokens.blue[600],
                  colorPrimaryHover: colorTokens.blue[500],
                },
              }}
            >
              <div className="flex items-center gap-1">
                <Switch
                  size="small"
                  checked={keepCreating}
                  onChange={(checked) => {
                    setKeepCreating(checked);
                  }}
                />
                <div className="text-neutral-900 text-base font-semibold">Keep Creating</div>
              </div>
            </ConfigProvider>
            <BaseButton
              type="primary"
              size="medium"
              disabled={isCreating || (!!props.showSpaceSelector && !selectedSpaceId)}
              onClick={handleCreate}
            >
              {isCreating ? 'Creating...' : `Create ${taskTitle}`}
            </BaseButton>
          </div>
        </div>
      }
    >
      <div
        className="flex"
        style={{ height: 540 }}
        onMouseDownCapture={handleBodyMouseDownCapture}
        onClickCapture={handleBodyClickCapture}
        onFocusCapture={handleBodyFocusCapture}
      >
        {/* Left Column - Title + Description */}
        <div className="flex-1 flex flex-col pl-7 pr-6 pt-4 pb-4 overflow-auto">
          <div className="shrink-0">
            <TitleEditableField
              initialValue={title}
              onChange={async (title) => setTitle(title)}
              className="text-brand-grey-dark text-xl font-semibold"
              inputRef={inputRef}
            />
          </div>
          <div className="flex flex-col flex-1 gap-2" style={{ minHeight: 'calc(100% - 70px)' }}>
            <span className="text-brand-grey-dark text-xs font-semibold cursor-text" onClick={focusDescriptionEditor}>Description</span>
            <div ref={descriptionContainerRef} className="flex-1 flex flex-col [&>div]:flex-1 [&>div]:flex [&>div]:flex-col [&>div>div]:flex-1">
              <RichTextEditor
                value={description}
                onChange={setDescription}
                placeholder="What is this task about?"
                minHeight="0"
                mode="description"
                hideImageUpload
                reserveToolbarSpace
                usersWithAccessToSpace={usersWithAccessToSpaceQuery.data?.map((userAccess) => userAccess.User) ?? []}
              />
            </div>
          </div>
        </div>

        {/* Right Column - Properties Sidebar */}
        <div className="flex" style={{ width: 376 }}>
          <Divider type="vertical" className="h-full m-0" />
          <div className="w-full px-6 py-4 overflow-auto">
            <div className="text-brand-grey-dark text-base font-semibold mb-3 cursor-default">Properties</div>

            {/* Space — shown only when opened from All Tasks */}
            {props.showSpaceSelector && (
              <div className="h-10 flex items-center justify-start gap-10">
                <div className="min-w-[84px] text-neutral-800 text-sm font-medium cursor-default">Space</div>
                <div className="flex items-center px-2 cursor-pointer">
                  <BaseDropdown
                    trigger={['click']}
                    open={spaceDropdownOpen}
                    onOpenChange={setSpaceDropdownOpen}
                    dropdownRender={() => (
                      <div className="flex flex-col gap-1 bg-brand-white shadow-md rounded-lg p-2 max-h-[300px] overflow-y-auto min-w-[160px]">
                        {(workspacesQuery.data ?? []).map((ws) => (
                          <div
                            key={ws.id}
                            className="px-2 py-1.5 text-sm text-neutral-1000 font-normal rounded hover:bg-neutral-100 cursor-pointer"
                            onClick={() => { setSelectedSpaceId(ws.id); setSpaceDropdownOpen(false); }}
                          >
                            {ws.name}
                          </div>
                        ))}
                      </div>
                    )}
                  >
                    <div className="h-full flex items-center px-1">
                      {selectedSpaceId
                        ? <div className="text-neutral-1000 text-sm font-normal">
                            {workspacesQuery.data?.find((ws) => ws.id === selectedSpaceId)?.name}
                          </div>
                        : <div className="text-neutral-700 text-sm font-normal">Select Space</div>
                      }
                    </div>
                  </BaseDropdown>
                </div>
              </div>
            )}

            {propertiesReady && (
              <>
            {/* Type */}
            <div className="h-10 flex items-center justify-start gap-10">
              <div className="min-w-[84px] text-neutral-800 text-sm font-medium cursor-default">Type</div>
              <div className="flex items-center px-2 cursor-pointer">
                <TaskTypeDropdown
                  type={taskType}
                  size="large"
                  onBlur={() => {}}
                  onSelect={async (type) => {
                    setTaskType(type);
                  }}
                  allowEpic={true}
                />
              </div>
            </div>

            {/* Epic */}
            {taskType !== TaskType.epic && (
              <div className="h-10 flex items-center justify-start gap-10">
                <div className="min-w-[84px] text-neutral-800 text-sm font-medium cursor-default">Epic</div>
                <div className="flex items-center px-2 cursor-pointer">
                  <EpicDropdown
                    epic={epic}
                    epics={epicsQuery.data ?? []}
                    onBlur={() => {}}
                    onSelect={(epic) => {
                      setEpic(epic ?? null);
                    }}
                    size="large"
                    disabled={!!parentTask}
                  />
                </div>
              </div>
            )}

            {/* Parent Task */}
            {taskType !== TaskType.epic && (
              <div className="h-10 flex items-center justify-start gap-10">
                <div className="min-w-[84px] text-neutral-800 text-sm font-medium cursor-default">Parent Task</div>
                <div className="flex items-center px-2 cursor-pointer">
                  <ParentTaskDropdown
                    task={parentTask}
                    options={(tasksQuery.data ?? []).filter((t) => t.parentTaskId === null && t.stage !== 'triage')}
                    epics={epicsQuery.data ?? []}
                    onBlur={() => {}}
                    onSelect={async (parentTask) => {
                      setParentTask(parentTask ?? null);
                      setEpic(
                        parentTask?.epicId ? (epicsQuery.data?.find((epic) => epic.id === parentTask.epicId) ?? null) : null
                      );
                    }}
                    size="large"
                  />
                </div>
              </div>
            )}

            {/* Stage */}
            {taskType !== TaskType.epic && (
              <div className="h-10 flex items-center justify-start gap-10">
                <div className="min-w-[84px] text-neutral-800 text-sm font-medium cursor-default">Stage</div>
                <div className="flex items-center px-2 cursor-pointer">
                  <SprintStatusDropdown
                    sprintStatus={sprintStatus}
                    sprintStatuses={sprintStatuses}
                    onSelect={(sprintStatus) => {
                      setSprintStatus(sprintStatus as SprintStatusType);
                    }}
                    onBlur={() => {}}
                    size="large"
                  />
                </div>
              </div>
            )}

            {/* Label */}
            {taskType !== TaskType.epic && (
              <div className="h-10 flex items-center justify-start gap-10">
                <div className="min-w-[84px] text-neutral-800 text-sm font-medium cursor-default">Label</div>
                <div className="flex items-center px-2 cursor-pointer">
                  <LabelDropdown
                    selectedLabels={labels}
                    availableLabels={spaceLabels}
                    onSelectionChange={(selectedLabels: SpaceLabel[]) => setLabels(selectedLabels)}
                    onBlur={() => {}}
                    size="large"
                    emptyElement={<div className="text-neutral-900 text-sm font-normal">Add Label</div>}
                  />
                </div>
              </div>
            )}

            {/* Owner */}
            <div className="h-10 flex items-center justify-start gap-10">
              <div className="min-w-[84px] text-neutral-800 text-sm font-medium cursor-default">Owner</div>
              <div className="flex items-center px-2 cursor-pointer">
                <OwnerDropdown
                  owner={assignee}
                  owners={usersWithAccessToSpaceQuery.data?.map((userAccess) => userAccess.User) ?? []}
                  onBlur={() => {}}
                  onSelect={(user) => setAssignee(user as User | null)}
                  size="small"
                  emptyElement={<div className="text-neutral-900 text-sm font-normal">Add Owner</div>}
                />
              </div>
            </div>

            {/* Status */}
            {taskType !== TaskType.epic && (
              <div className="h-10 flex items-center justify-start gap-10">
                <div className="min-w-[84px] text-neutral-800 text-sm font-medium cursor-default">Status</div>
                <div className="flex items-center px-2 cursor-pointer">
                  <TaskStatusDropdown
                    taskStatusId={status.id}
                    workspaceId={effectiveSpaceId || undefined}
                    onSelect={(status) => {
                      setStatus(status);
                    }}
                    onBlur={() => {}}
                    size="large"
                  />
                </div>
              </div>
            )}
              </>
            )}

            {/* Priority */}
            {taskType !== TaskType.epic && (
              <div className="h-10 flex items-center justify-start gap-10">
                <div className="min-w-[84px] text-neutral-800 text-sm font-medium cursor-default">Priority</div>
                <div className="flex items-center px-2 cursor-pointer">
                  <PriorityDropdown
                    priority={priority ?? PriorityOptions.NoPriority}
                    size="large"
                    // Normalize "No priority" back to null so it matches the initial/default state —
                    // otherwise re-selecting it counts as a change and pops the unsaved dialog. #2459
                    onSelect={(priority) => setPriority(priority === PriorityOptions.NoPriority ? null : priority)}
                    onBlur={() => {}}
                  />
                </div>
              </div>
            )}

            {/* Estimate */}
            {propertiesReady && taskType !== TaskType.epic && (
              <div className="h-10 flex items-center justify-start gap-10">
                <div className="min-w-[84px] text-neutral-800 text-sm font-medium cursor-default">Estimate</div>
                <div className="flex items-center px-2 cursor-pointer">
                  <EstimateDropdown
                    estimate={estimate}
                    size="large"
                    onSelect={(estimate) => setEstimate(estimate)}
                  />
                </div>
              </div>
            )}

            {/* Start Date */}
            <div className="h-10 flex items-center justify-start gap-10">
              <div className="min-w-[84px] text-neutral-800 text-sm font-medium cursor-default">Start Date</div>
              <div className="flex items-center px-2 cursor-pointer">
                <DeadlineEditableField
                  editingDisabled={false}
                  startDate={startDate}
                  targetDate={deadline}
                  mode="startDate"
                  onChange={(date) => setStartDate(date)}
                />
              </div>
            </div>

            {/* Target Date */}
            <div className="h-10 flex items-center justify-start gap-10">
              <div className="min-w-[84px] text-neutral-800 text-sm font-medium cursor-default">Target Date</div>
              <div className="flex items-center px-2 cursor-pointer">
                <DeadlineEditableField
                  editingDisabled={false}
                  startDate={startDate}
                  targetDate={deadline}
                  mode="targetDate"
                  onChange={(date) => setDeadline(date)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
    <UnsavedChangesModal
      open={showUnsavedModal}
      onDiscard={() => {
        setShowUnsavedModal(false);
        resetFields();
        onCancel();
      }}
      onCancel={() => setShowUnsavedModal(false)}
      onSave={async () => {
        setShowUnsavedModal(false);
        await handleCreate();
      }}
      message="You have unsaved changes. How would you like to proceed?"
    />
    </>
  );
};
